<p-toast />
<p-confirmDialog />
<div class="px-6 py-4">
  <div class="flex justify-content-between align-items-center mb-4">
    <p class="text-xl font-semibold">{{ 'layout.configurations.staffedRoles.title' | translate }}</p>
    
    <div class="flex gap-3">
      <p-dropdown 
        [options]="fiscalYearOptions" 
        [(ngModel)]="selectedFiscalYear" 
        optionLabel="label"
        optionValue="value"
        (onChange)="onFilterChange()"
        [placeholder]="'layout.configurations.staffedRoles.selectFiscalYear' | translate"
        class="w-12rem">
      </p-dropdown>
      
      <p-fileUpload 
      *emPermission="Permission.CONFIGURATION_WRITE"  
      mode="basic"
        [chooseLabel]="'layout.configurations.staffedRoles.importNewStaffedRole' | translate"
        chooseIcon="pi pi-upload"
        accept=".xlsx,.xls"
        [maxFileSize]="10000000"
        (onSelect)="onImport($event)"
        [auto]="true"
        [disabled]="isImporting"
        styleClass="p-button-info">
      </p-fileUpload>
        <p-button
        icon="pi pi-download"
        severity="help"
        [label]="'layout.configurations.staffedRoles.export' | translate"
        (onClick)="onExport()"
        [pTooltip]="'layout.configurations.staffedRoles.exportTooltip' | translate"
        tooltipPosition="top">
      </p-button>

      <p-button *emPermission="Permission.INVOICING_ADMIN" 
        icon="pi pi-sync" 
        severity="info"
        [label]="'layout.configurations.staffedRoles.rollover.button' | translate"
        (onClick)="openRolloverDialog()">
      </p-button>

      <p-button *emPermission="Permission.CONFIGURATION_WRITE" class="p-button-success" icon="pi pi-plus" severity="success" (onClick)="onAdd()"></p-button>
    </div>
  </div>
 <!-- Filter Section -->
  <div class="flex mb-3 align-items-center">
    <div class="filter-input-group">
      <input
        type="text"
        pInputText
        [(ngModel)]="roleFilter"
        (keyup.enter)="onRoleFilterClick()"
        (ngModelChange)="onRoleFilterClick()"
        [placeholder]="'layout.configurations.staffedRoles.filterByRole' | translate"
        class="filter-input"
      />
      <p-button
        *ngIf="roleFilter && roleFilter.trim().length > 0"
        icon="pi pi-times"
        (onClick)="clearRoleFilter()"
        styleClass="p-button-text p-button-plain clear-icon-btn"
        [pTooltip]="'layout.configurations.staffedRoles.clearFilter' | translate"
        tooltipPosition="top">
      </p-button>
    </div>
  </div>
  <p-table [value]="staffedRoles" [paginator]="true" [rows]="6" [rowsPerPageOptions]="[6, 10, 20]"
    styleClass="w-full p-datatable-sm">
    <ng-template pTemplate="header">
      <tr>
        <th scope="col" pSortableColumn="role" class="bg-white">
          {{ 'layout.configurations.staffedRoles.group' | translate }}
          <p-sortIcon field="role" />
        </th>
        <th scope="col" class="bg-white">{{ 'layout.configurations.staffedRoles.fiscalYear' | translate }}</th>
        <!-- Dynamic currency columns — same order as Currency page -->
        <th scope="col" class="bg-white" *ngFor="let currency of currencies">
          {{ currency }}
        </th>
        <th *emPermission="Permission.CONFIGURATION_WRITE" scope="col" class="bg-white w-10rem">{{ 'layout.configurations.staffedRoles.actions' | translate }}</th>
      </tr>
    </ng-template>

  <ng-template pTemplate="body" let-staffedRole>
    <tr>
      <td>{{ staffedRole.role }}</td>
      <td>{{ staffedRole.fiscalYear }}</td>
       <td *ngFor="let currency of currencies">
          {{ staffedRole.convertedAmount?.[currency]?.convertedAmount != null
              ? (staffedRole.convertedAmount[currency].convertedAmount | number:'1.2-2')
              : '-' }}
        </td>
      <td>
        <ng-container *emPermission="Permission.CONFIGURATION_WRITE">
        <div class="flex gap-2">
          <p-button icon="pi pi-pencil" [outlined]="true" (onClick)="onEdit(staffedRole)"></p-button>
          <p-button icon="pi pi-trash" severity="danger" [outlined]="true"
            (onClick)="onDelete($event, staffedRole.id)"></p-button>
        </div></ng-container>
      </td>
    </tr>
  </ng-template>
</p-table>
</div>

<!-- Rollover Dialog -->
<p-dialog 
  appendTo="body"
  [header]="'layout.configurations.staffedRoles.rollover.title' | translate"
  [(visible)]="rolloverDialogVisible"
  [modal]="true"
  [position]="'center'"
  [draggable]="false"
  [style]="{ width: '500px' }"
  [closable]="true"
  (onHide)="closeRolloverDialog()">

  <!-- Step 1: Select FYs -->
  <div *ngIf="rolloverStep === 'select'" class="flex flex-column gap-3">
    <p class="mb-2">{{ 'layout.configurations.staffedRoles.rollover.description' | translate }}</p>
    
    <div class="field">
      <label class="block mb-2 font-bold">{{ 'layout.configurations.staffedRoles.rollover.sourceYear' | translate }}</label>
      <p-dropdown 
        [options]="fiscalYearOptions" 
        [(ngModel)]="rolloverSourceFy"
        optionLabel="label"
        optionValue="value"
        [placeholder]="'layout.configurations.staffedRoles.rollover.selectSource' | translate"
        appendTo="body"
        styleClass="w-full">
      </p-dropdown>
    </div>

    <div class="field">
      <label class="block mb-2 font-bold">{{ 'layout.configurations.staffedRoles.rollover.targetYear' | translate }}</label>
      <p-dropdown 
        [options]="fiscalYearOptions" 
        [(ngModel)]="rolloverTargetFy"
        optionLabel="label"
        optionValue="value"
        [placeholder]="'layout.configurations.staffedRoles.rollover.selectTarget' | translate"
        appendTo="body"
        styleClass="w-full">
      </p-dropdown>
    </div>
  </div>

  <!-- Step 2: Preview -->
  <div *ngIf="rolloverStep === 'preview' && rolloverPreview" class="flex flex-column gap-2">
    <p class="mb-2">{{ 'layout.configurations.staffedRoles.rollover.previewMessage' | translate }}</p>
    
    <div class="grid">
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.sourceRoles' | translate }}:</strong> {{ rolloverPreview.totalSourceRoles }}
      </div>
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.sourceEmployees' | translate }}:</strong> {{ rolloverPreview.totalSourceEmployees }}
      </div>
    </div>

    <p-divider></p-divider>

    <div class="grid">
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.toCreateRoles' | translate }}:</strong> 
        <span class="text-green-600">{{ rolloverPreview.toCreateRoles }}</span>
      </div>
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.toCreateEmployees' | translate }}:</strong> 
        <span class="text-green-600">{{ rolloverPreview.toCreateEmployees }}</span>
      </div>
    </div>
    <div class="grid">
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.toSkipRoles' | translate }}:</strong> 
        <span class="text-orange-500">{{ rolloverPreview.toSkipRoles }}</span>
      </div>
      <div class="col-6">
        <strong>{{ 'layout.configurations.staffedRoles.rollover.toSkipEmployees' | translate }}:</strong> 
        <span class="text-orange-500">{{ rolloverPreview.toSkipEmployees }}</span>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div *ngIf="rolloverStep === 'select'">
      <p-button 
        [label]="'layout.configurations.staffedRoles.rollover.previewButton' | translate"
        (onClick)="loadRolloverPreview()"
        [disabled]="!rolloverSourceFy || !rolloverTargetFy || rolloverLoading"
        [loading]="rolloverLoading">
      </p-button>
    </div>

    <div *ngIf="rolloverStep === 'preview'">
      <p-button 
        [label]="'layout.configurations.staffedRoles.rollover.cancel' | translate"
        severity="secondary"
        (onClick)="closeRolloverDialog()"
        [disabled]="rolloverLoading"
        class="mr-2">
      </p-button>
      <p-button 
        [label]="'layout.configurations.staffedRoles.rollover.executeButton' | translate"
        severity="warn"
        (onClick)="executeRollover()"
        [loading]="rolloverLoading"
        [disabled]="rolloverLoading">
      </p-button>
    </div>
  </ng-template>
</p-dialog>